import BackendService from 'BackendService';
import Contact from '../../../models/Contact';
import { InvestmentAllocation, InvestmentEntity } from '../../../models/InvestmentEntity';
import Investor from '../../../models/Investor';

export interface InvestorRefreshResult {
  investor: Investor;
  allocations: InvestmentAllocation[];
}

export async function fetchInvestorAndContact(
  investorId: string,
): Promise<{ investor: Investor; contact: Contact }> {
  const backend = new BackendService();
  const investor = await backend.getInvestor(investorId);
  const contact = await backend.getContact(investor.contactId);
  return { investor, contact };
}

export async function refreshInvestorDataForInvestor(
  investor: Investor,
): Promise<InvestorRefreshResult | null> {
  try {
    const backend = new BackendService();
    const [updatedInvestor, allocationsData] = await Promise.all([
      backend.getInvestor(investor.id),
      backend.getInvestorAllocations(investor.id, false),
    ]);

    return {
      investor: {
        ...updatedInvestor,
        totalAllocatedAmount: Number(allocationsData.totalAllocated) || 0,
        committedInvestment: Number(allocationsData.committedInvestment) || 0,
      },
      allocations: allocationsData.allocations,
    };
  } catch (e) {
    console.error('Failed to refresh investor data:', e);
    return null;
  }
}

export interface EntitiesLoadResult {
  entities: InvestmentEntity[];
  allocations: InvestmentAllocation[];
  investorTotals?: Pick<Investor, 'totalAllocatedAmount' | 'committedInvestment'>;
}

export async function fetchEntitiesForContact(
  contact: Contact,
  investor: Investor | null,
): Promise<EntitiesLoadResult> {
  const backend = new BackendService();
  const entities = await backend.getInvestmentEntitiesForUserAsOwner(contact);

  if (!investor) {
    return { entities, allocations: [] };
  }

  const allocationsData = await backend.getInvestorAllocations(investor.id, false);
  return {
    entities,
    allocations: allocationsData.allocations ?? [],
    investorTotals: {
      totalAllocatedAmount: Number(allocationsData.totalAllocated) || 0,
      committedInvestment: Number(allocationsData.committedInvestment) || 0,
    },
  };
}

export function investorDataChanged(
  prevInvestor: Investor | null | undefined,
  nextInvestor: Investor,
  prevAllocations: InvestmentAllocation[] | undefined,
  nextAllocations: InvestmentAllocation[],
): boolean {
  const investorChanged =
    prevInvestor?.totalAllocatedAmount !== nextInvestor.totalAllocatedAmount ||
    prevInvestor?.committedInvestment !== nextInvestor.committedInvestment;

  const allocationsChanged = JSON.stringify(prevAllocations) !== JSON.stringify(nextAllocations);

  return investorChanged || allocationsChanged;
}
